import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { postsAPI, clientsAPI, credentialsAPI } from '../services/api.js';
import CredentialsModal from "../pages/CredentialsModal";
import {
  Calendar,
  Plus,
  Link,
  Users,
  LogOut,
  X,
  ChevronLeft,
  ChevronRight,
  Upload,
  Filter,
  AlertCircle,
  Image as ImageIcon,
  Film,
  LayoutGrid,
  FileText,
  Video
} from 'lucide-react';
import './Dashboard.css';

const Dashboard = () => {
  const { user, logout, token } = useAuth();
  const navigate = useNavigate();

  const [currentDate, setCurrentDate] = useState(new Date());
  const [showModal, setShowModal] = useState(false);
  const [posts, setPosts] = useState([]);
  const [clients, setClients] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const [selectedCredential, setSelectedCredential] = useState(null);
  const [selectedClientFilter, setSelectedClientFilter] = useState('all');
  const [activeTimezone, setActiveTimezone] = useState('UTC');
  const [openCreds, setOpenCreds] = useState(false);

  // Upload form state
  const [formData, setFormData] = useState({
    name: '',
    platforms: [],
    caption: '',
    ai_caption: false,
    scheduleDateTime: '',
    files: [],
    client_id: '',
    post_type: 'auto' // ✅ NEW: post_type selection
  });

  const platformColors = {
    instagram: '#E4405F',
    facebook: '#1877F2',
    youtube: '#FF0000',
    linkedin: '#0A66C2',
    twitter: '#1DA1F2'
  };

  // ✅ NEW: Post type icons
  const postTypeIcons = {
    image: <ImageIcon size={14} />,
    video: <Film size={14} />,
    reel: <Video size={14} />,
    carousel: <LayoutGrid size={14} />,
    text: <FileText size={14} />,
    story: <Film size={14} />
  };

  // ✅ NEW: Status colors for new status field
  const getNewStatusColor = (status) => {
    switch (status) {
      case 'READY': return '#6c757d';
      case 'PROCESSING': return '#ffc107';
      case 'WAITING': return '#17a2b8';
      case 'DONE': return '#28a745';
      case 'FAILED': return '#dc3545';
      case 'FAILED_LATE': return '#d9534f';
      case 'CANCELLED': return '#6c757d';
      case 'MANUAL_DONE': return '#5cb85c';
      default: return '#6c757d';
    }
  };

  const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const monthNames = [
    'January','February','March','April','May','June','July','August','September','October','November','December'
  ];

  const toTzDate = (date, timeZone) => {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).formatToParts(date);

    const y = parts.find(p => p.type === 'year').value;
    const m = parts.find(p => p.type === 'month').value;
    const d = parts.find(p => p.type === 'day').value;

    return new Date(`${y}-${m}-${d}T00:00:00`);
  };

  const parseUtcFromDb = useCallback((dt) => {
    if (!dt) return null;

    let iso = dt;

    if (!dt.includes('T') && dt.includes(' ')) {
      iso = dt.replace(' ', 'T') + 'Z';
    }

    if (iso.includes('T') && !iso.endsWith('Z') && !/[+-]\d\d:\d\d$/.test(iso)) {
      iso = iso + 'Z';
    }

    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? null : d;
  }, []);

  const getMediaCount = (post) => {
    if (Array.isArray(post.media_url)) return post.media_url.length;
    if (post.media_url) return 1;
    return 0;
  };

  const isMultiMediaPlatform = () => {
    return formData.platforms.includes('instagram') || formData.platforms.includes('facebook');
  };

  // ✅ NEW: Determine if post_type selection should be shown
  const shouldShowPostTypeSelector = () => {
    // Show post_type selector only for Instagram
    return formData.platforms.includes('instagram');
  };

  // ✅ NEW: Get available post types based on selected platforms
  const getAvailablePostTypes = () => {
    const hasInstagram = formData.platforms.includes('instagram');
    const hasLinkedIn = formData.platforms.includes('linkedin');
    const fileCount = formData.files.length;

    if (hasInstagram) {
      if (fileCount === 0) return ['text'];
      if (fileCount === 1) return ['auto', 'image', 'reel', 'story'];
      if (fileCount > 1) return ['auto', 'carousel'];
      return ['auto', 'image', 'reel', 'story', 'carousel'];
    }

    if (hasLinkedIn && fileCount === 0) {
      return ['text'];
    }

    return ['auto'];
  };

  const fetchPosts = useCallback(async () => {
    try {
      if (!user?.id) return;

      const response = await postsAPI.getAllPosts(user.id, null);
      if (response?.success) {
        console.log('🔍 Raw posts from API:', response.posts);
        setPosts(Array.isArray(response.posts) ? response.posts : []);
      } else {
        setPosts([]);
      }
    } catch (err) {
      console.error('Error fetching posts:', err);
      setPosts([]);
    }
  }, [user]);

  const fetchClients = useCallback(async () => {
    try {
      if (!token) return;

      const response = await clientsAPI.getMyClients(token);
      if (response?.success) {
        setClients(Array.isArray(response.clients) ? response.clients : []);
      } else {
        setClients([]);
      }
    } catch (err) {
      console.error('Error fetching clients:', err);
      setClients([]);
    }
  }, [token]);

  const fetchCredentialsForPlatforms = useCallback(async () => {
    try {
      if (!token) return;

      const clientId = user?.user_type === 'organization' ? formData.client_id : null;

      const credentialPromises = formData.platforms.map((platform) =>
        credentialsAPI.getMyCredentials(token, clientId, platform)
      );

      const responses = await Promise.all(credentialPromises);

      const allHaveCredentials = responses.every(
        (response) => response.success && response.credentials.length > 0
      );

      if (allHaveCredentials) {
        const allCredentials = responses.map((response, index) => ({
          platform: formData.platforms[index],
          credential: response.credentials[0]
        }));
        setSelectedCredential(allCredentials);
      } else {
        setSelectedCredential(null);
      }
    } catch (err) {
      console.error('Error fetching credentials:', err);
      setSelectedCredential(null);
    }
  }, [token, user, formData.client_id, formData.platforms]);

  useEffect(() => {
    if (user?.id) fetchPosts();
  }, [user, fetchPosts]);

  useEffect(() => {
    if (user?.user_type === 'organization' && token) fetchClients();
  }, [user, token, fetchClients]);

  useEffect(() => {
    if (!user) return;

    if (user.user_type === 'individual') {
      setActiveTimezone(user.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone);
      return;
    }

    if (user.user_type === 'organization') {
      const selectedClient = clients.find((c) => c.id === parseInt(formData.client_id));
      if (selectedClient?.timezone) {
        setActiveTimezone(selectedClient.timezone);
      } else {
        setActiveTimezone(user.timezone || 'UTC');
      }
    }
  }, [user, clients, formData.client_id]);

  useEffect(() => {
    console.log('🕐 Active Timezone:', activeTimezone);
    console.log('👤 User Timezone:', user?.timezone);
    console.log('🏢 User Type:', user?.user_type);
  }, [activeTimezone, user]);

  useEffect(() => {
    if (formData.platforms.length > 0 && token) {
      fetchCredentialsForPlatforms();
    } else {
      setSelectedCredential(null);
    }
  }, [formData.platforms, formData.client_id, token, fetchCredentialsForPlatforms]);

  useEffect(() => {
    if (formData.post_type === 'story' && formData.files.length > 1) {
      setFormData((prev) => ({ ...prev, files: prev.files.slice(0, 1) }));
      setError('Story only supports 1 file. Extra files removed.');
    }
  }, [formData.post_type]);

  const getFilteredPosts = () => {
    if (selectedClientFilter === 'all') return posts;
    return posts.filter((post) => post.client_id === parseInt(selectedClientFilter));
  };

  const getWeekDates = () => {
    const base = toTzDate(currentDate, activeTimezone);

    const start = new Date(base);
    start.setDate(start.getDate() - start.getDay());

    const dates = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      dates.push(d);
    }
    return dates;
  };

  const weekDates = getWeekDates();

  const getPostsForDate = (date) => {
    const filteredPosts = getFilteredPosts();

    const calendarDateStr = new Intl.DateTimeFormat('en-CA', {
      timeZone: activeTimezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).format(date);

    return filteredPosts.filter((post) => {
      const d = parseUtcFromDb(post.schedule_datetime);
      if (!d) return false;

      const postDateStr = new Intl.DateTimeFormat('en-CA', {
        timeZone: activeTimezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).format(d);

      return postDateStr === calendarDateStr;
    });
  };

  const formatTime = (utcDateString) => {
    const d = parseUtcFromDb(utcDateString);
    
    console.log('📅 Original UTC string:', utcDateString);
    console.log('📅 Parsed Date object:', d);
    console.log('📅 Active Timezone for formatting:', activeTimezone);
    
    if (!d) return 'Invalid Time';
    
    const formatted = new Intl.DateTimeFormat('en-US', {
      timeZone: activeTimezone,
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }).format(d);
    
    console.log('📅 Formatted time:', formatted);
    return formatted;
  };

  const handlePrevWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() - 7);
    setCurrentDate(newDate);
  };

  const handleNextWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + 7);
    setCurrentDate(newDate);
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const handlePlatformChange = (platform) => {
    setFormData((prev) => ({
      ...prev,
      platforms: prev.platforms.includes(platform)
        ? prev.platforms.filter((p) => p !== platform)
        : [...prev.platforms, platform]
    }));
    setError('');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'uploading': return '#ffc107';
      case 'completed': return '#28a745';
      case 'failed': return '#dc3545';
      case 'scheduled': return '#17a2b8';
      case 'ready': return '#6c757d';
      default: return '#6c757d';
    }
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);

    // ✅ Story only allows 1 file
    if (formData.post_type === 'story') {
      if (selectedFiles.length > 1) {
        setError('Story only supports a single image or video');
        return;
      }
      setFormData((prev) => ({ ...prev, files: selectedFiles.slice(0, 1) }));
      setError('');
      return;
    }

    if (isMultiMediaPlatform()) {
      if (selectedFiles.length > 10) {
        setError('You can upload maximum 10 media files for Instagram/Facebook');
        return;
      }
      setFormData((prev) => ({ ...prev, files: selectedFiles }));
    } else {
      setFormData((prev) => ({ ...prev, files: selectedFiles.slice(0, 1) }));
    }
    setError('');
  };

  const handleInputChange = (e) => {
    const { name, type, value, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    setError('');
  };

  const handleSubmit = async () => {
    setError('');

    if (formData.platforms.length === 0) {
      setError('Please select at least one platform');
      return;
    }

    if (user?.user_type === 'organization' && !formData.client_id) {
      setError('Please select a client');
      return;
    }

    const hasMedia = formData.files && formData.files.length > 0;
    const isLinkedInOnly = formData.platforms.length === 1 && formData.platforms.includes('linkedin');

    if (!hasMedia) {
      if (!isLinkedInOnly) {
        setError('Media is required for the selected platform(s). Only LinkedIn supports text-only posts.');
        return;
      }
    }

    if (!selectedCredential) {
      setError(`Please connect accounts for: ${formData.platforms.join(', ')}`);
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      const uploadFormData = new FormData();

      if (hasMedia) {
        formData.files.forEach((file) => {
          uploadFormData.append('media', file);
        });
      }

      uploadFormData.append('name', formData.name);
      uploadFormData.append('platforms', JSON.stringify(formData.platforms));
      uploadFormData.append('caption', formData.caption);
      uploadFormData.append('ai_caption', formData.ai_caption);
      uploadFormData.append('scheduleDateTime', formData.scheduleDateTime);
      uploadFormData.append('timezone', activeTimezone);
      uploadFormData.append('user_id', user.id);

      // ✅ NEW: Add post_type if manually selected (not 'auto')
      // if (formData.post_type && formData.post_type !== 'auto') {
        uploadFormData.append('post_type', formData.post_type);
      // }

      if (user?.user_type === 'organization' && formData.client_id) {
        uploadFormData.append('client_id', formData.client_id);
      }

      if (selectedCredential && Array.isArray(selectedCredential)) {
        uploadFormData.append('credentials', JSON.stringify(selectedCredential));
      }

      const response = await postsAPI.createPost(uploadFormData, (progress) => {
        setUploadProgress(progress);
      });

      if (response.success) {
        const mediaCount = hasMedia ? formData.files.length : 0;
        alert(
          `✅ Success! ${response.postsCreated || formData.platforms.length} post(s) scheduled${
            mediaCount > 0 ? ` with ${response.mediaFilenames?.length || mediaCount} media file(s)` : ' (text-only)'
          }!`
        );

        await fetchPosts();

        setFormData({
          name: '',
          platforms: [],
          caption: '',
          ai_caption: false,
          scheduleDateTime: '',
          files: [],
          client_id: '',
          post_type: 'auto'
        });

        setSelectedCredential(null);
        setShowModal(false);
      }
    } catch (err) {
      console.error('❌ Upload failed:', err);
      setError('Upload failed: ' + (err.message || 'Unknown error'));
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const getClientName = (clientId) => {
    if (!clientId) return null;
    const client = clients.find((c) => c.id === clientId);
    return client ? client.client_name : 'Unknown Client';
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const handleClientFilterChange = (e) => {
    setSelectedClientFilter(e.target.value);
  };

  // ✅ NEW: Render post card with new fields
  const renderPostCard = (post) => {
    const mediaCount = getMediaCount(post);

    const clientTime =
      user?.user_type === 'organization' && post.client_timezone
        ? (() => {
            const d = parseUtcFromDb(post.schedule_datetime);
            if (!d) return null;
            return new Intl.DateTimeFormat('en-US', {
              timeZone: post.client_timezone,
              hour: 'numeric',
              minute: '2-digit',
              hour12: true
            }).format(d);
          })()
        : null;

    // ✅ NEW: Check for errors
    const hasError = post.status === 'FAILED' || post.status === 'FAILED_LATE';

    return (
      <div
        key={post.id}
        className="post-card"
        style={{
          background: (platformColors[post.platform] || '#999') + '15',
          borderLeft: `3px solid ${platformColors[post.platform] || '#999'}`,
          borderColor: hasError ? '#dc3545' : undefined // ✅ Red border for errors
        }}
        onClick={() => navigate(`/posts/${post.id}`)}
      >
        <div className="post-card-header">
          <div className="post-time">{formatTime(post.schedule_datetime)}</div>
          
          {/* ✅ NEW: Show both upload_status and status */}
          {/* ✅ Show only status */}
          <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
            {post.status && (
              <span
                className="post-status-badge"
                style={{
                  background: getNewStatusColor(post.status),
                  color: 'white',
                  fontSize: '10px',
                  padding: '2px 6px'
                }}
                title={`Status: ${post.status}`}
              >
                {post.status}
              </span>
            )}
          </div>

        </div>

        <div className="post-name">{post.name}</div>

        {user?.user_type === 'organization' && post.client_id && (
          <div className="post-client">
            <Users size={12} />
            {getClientName(post.client_id)}
          </div>
        )}

        {clientTime && (
          <div className="post-subtime">
            Client time: {clientTime} ({post.client_timezone})
          </div>
        )}

        <div className="post-caption">
          {post.caption && post.caption.length > 60 ? post.caption.substring(0, 60) + '...' : post.caption}
        </div>

        <div className="post-footer">
          <span className="post-platform">
            📱 {post.platform?.charAt(0)?.toUpperCase() + post.platform?.slice(1)}
          </span>

          {/* ✅ NEW: Show post_type icon */}
          {post.post_type && (
            <span 
              className="post-type-badge" 
              title={`Type: ${post.post_type}`}
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '4px',
                fontSize: '11px',
                color: '#666'
              }}
            >
              {postTypeIcons[post.post_type]}
              {post.post_type}
            </span>
          )}

          {mediaCount > 0 && (
            <span className="post-has-media" title={`${mediaCount} media file(s)`}>
              🎬 {mediaCount > 1 ? `×${mediaCount}` : ''}
            </span>
          )}
        </div>

        {/* ✅ NEW: Show error message if failed */}
        {hasError && post.last_error_message && (
          <div 
            className="post-error-message"
            style={{
              marginTop: '8px',
              padding: '6px 8px',
              background: '#fff3cd',
              border: '1px solid #ffc107',
              borderRadius: '4px',
              fontSize: '11px',
              color: '#856404',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '6px'
            }}
          >
            <AlertCircle size={14} style={{ flexShrink: 0, marginTop: '1px' }} />
            <span>{post.last_error_message}</span>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="calendar-dashboard">
      <div className="dashboard-container">
        {/* Header */}
        <header className="dashboard-header">
          <div className="dashboard-logo">
            <div className="logo-icon">Q</div>
            <span>Quick Socials</span>
          </div>

          <div className="search-container">
            <span className="search-icon">🔍</span>
            <input type="text" className="search-input" placeholder="Search" />
          </div>

          <div className="header-right">
            <button className="new-post-btn" onClick={() => setShowModal(true)}>
              New Post
            </button>

            <div className="user-profile">
              <div className="user-avatar">
                {user?.name?.split(' ').map((n) => n[0]).join('').toUpperCase()}
              </div>
              <div className="user-info">
                <div className="user-name">{user?.name}</div>
                <div className="user-type">{user?.user_type === 'individual' ? 'Individual' : 'Organizational'}</div>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <div className="dashboard-main">
          {/* Left Sidebar */}
          <aside className="sidebar">
            <button className="sidebar-item active">
              <Calendar size={20} />
              Calendar
            </button>

            {user?.user_type === 'individual' && (
              <button className="sidebar-item" onClick={() => setOpenCreds(true)}>
                <Link size={20} />
                Connect Accounts
              </button>
            )}

            {user?.user_type === 'organization' && (
              <button className="sidebar-item" onClick={() => navigate('/clients')}>
                <Users size={20} />
                Manage Clients
              </button>
            )}

            <button className="sign-out-btn" onClick={handleLogout}>
              <LogOut size={16} />
              Sign Out
            </button>
          </aside>

          {/* Main Content */}
          <main className="main-content">
            <div className="calendar-header">
              <h1 className="calendar-title">Calendar</h1>

              <div className="calendar-controls">
                <div className="month-selector">
                  {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
                  <Calendar size={16} />
                </div>

                <button className="nav-btn" onClick={handlePrevWeek}>
                  <ChevronLeft size={18} />
                </button>
                <button className="today-btn" onClick={handleToday}>
                  Today
                </button>
                <button className="nav-btn" onClick={handleNextWeek}>
                  <ChevronRight size={18} />
                </button>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                {user?.user_type === 'organization' && clients.length > 0 && (
                  <div className="client-filter">
                    <Filter size={16} />
                    <select
                      value={selectedClientFilter}
                      onChange={handleClientFilterChange}
                      className="client-filter-select"
                    >
                      <option value="all">All Clients</option>
                      {clients.map((client) => (
                        <option key={client.id} value={client.id}>
                          {client.client_name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <button className="post-schedule-btn" onClick={() => setShowModal(true)}>
                  <span className="schedule-badge">{getFilteredPosts().length}</span>
                  Post Schedule
                </button>
              </div>
            </div>

            {/* Calendar Grid */}
            <div className="calendar-grid">
              {weekDates.map((date, index) => {
                const todayCheck = isToday(date);
                const dayPosts = getPostsForDate(date);

                return (
                  <div key={index} className="calendar-day">
                    <div className={`day-header ${todayCheck ? 'today' : ''}`}>
                      <div className="day-name">{daysOfWeek[date.getDay()]}</div>
                      <div className={`day-number ${todayCheck ? 'today' : ''}`}>{date.getDate()}</div>
                    </div>

                    <div className="day-posts">{dayPosts.map((post) => renderPostCard(post))}</div>
                  </div>
                );
              })}
            </div>
          </main>
        </div>
      </div>

      {/* Modal for Creating Post */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Schedule Social Media Post</h2>
              <button className="btn-close" onClick={() => setShowModal(false)}>
                <X size={24} />
              </button>
            </div>

            <div className="modal-body">
              {error && <div className="error-message">{error}</div>}

              {!selectedCredential && formData.platforms.length > 0 && (
                <div className="warning-message">⚠️ Please connect accounts for: {formData.platforms.join(', ')}</div>
              )}

              {selectedCredential && formData.platforms.length > 0 && (
                <div className="success-message">✅ All selected platforms connected ({formData.platforms.join(', ')})</div>
              )}

              {formData.platforms.length > 1 && (!formData.files || formData.files.length === 0) && (
                <div className="warning-message">
                  ⚠️ Text-only posts are only supported on LinkedIn. Please add media files or select only LinkedIn.
                </div>
              )}

              {formData.platforms.length === 1 && formData.platforms.includes('linkedin') && (
                <div className="info-message">
                  💡 LinkedIn supports text-only posts. Media is optional.
                </div>
              )}

              {user?.user_type === 'organization' && (
                <div className="form-group">
                  <label className="form-label">Select Client *</label>
                  <select name="client_id" value={formData.client_id} onChange={handleInputChange}>
                    <option value="">Choose a client</option>
                    {clients.map((client) => (
                      <option key={client.id} value={client.id}>
                        {client.client_name} {client.client_company_name ? `(${client.client_company_name})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Enter post name"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Platforms *</label>
                <div className="platform-chips">
                  {['instagram', 'facebook', 'youtube', 'linkedin', 'twitter'].map((platform) => (
                    <label
                      key={platform}
                      className={`platform-chip ${formData.platforms.includes(platform) ? 'selected' : ''}`}
                      style={{
                        borderColor: formData.platforms.includes(platform) ? platformColors[platform] : '#E1DFDD',
                        background: formData.platforms.includes(platform) ? platformColors[platform] + '15' : 'transparent'
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={formData.platforms.includes(platform)}
                        onChange={() => handlePlatformChange(platform)}
                      />
                      <span>{platform}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* ✅ NEW: Post Type Selector (only for Instagram) */}
              {shouldShowPostTypeSelector() && (
                <div className="form-group">
                  <label className="form-label">
                    Post Type
                    <span style={{ fontSize: '12px', color: '#666', marginLeft: '6px', fontWeight: 'normal' }}>
                      (Auto-detect or select manually)
                    </span>
                  </label>
                  <select
                    name="post_type"
                    value={formData.post_type}
                    onChange={handleInputChange}
                    className="post-type-select"
                  >
                    <option value="auto">Auto-detect from media</option>
                    {getAvailablePostTypes().filter(t => t !== 'auto').map((type) => (
                      <option key={type} value={type}>
                        {type.charAt(0).toUpperCase() + type.slice(1)}
                      </option>
                    ))}
                  </select>
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                    {formData.post_type === 'story'
                      ? '📖 Story: 1 image (JPEG, max 8MB) or 1 video (MP4, max 100MB, max 60sec). No captions supported.'
                      : '💡 For Instagram: Choose "Reel" for vertical videos, "Image" for photos, "Carousel" for multiple media'}
                  </div>
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Caption *</label>
                <textarea
                  name="caption"
                  value={formData.caption}
                  onChange={handleInputChange}
                  rows="4"
                  placeholder="Enter your caption here..."
                />

                <div className="checkbox-group" style={{ marginTop: '8px' }}>
                  <label>
                    <input
                      type="checkbox"
                      name="ai_caption"
                      checked={formData.ai_caption}
                      onChange={handleInputChange}
                    />
                    {' '}AI Generated Caption
                  </label>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">
                  Schedule Date &amp; Time *{' '}
                  <span style={{ fontSize: '12px', color: '#666', marginLeft: '6px' }}>
                    ({activeTimezone})
                  </span>
                </label>
                <input
                  type="datetime-local"
                  name="scheduleDateTime"
                  value={formData.scheduleDateTime}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group">
                <label className="form-label">
                  Upload Image/Video 
                  {!(formData.platforms.length === 1 && formData.platforms.includes('linkedin')) && ' *'}
                  {isMultiMediaPlatform() && ' (up to 10 files)'}
                  {formData.platforms.length === 1 && formData.platforms.includes('linkedin') && 
                    <span style={{ fontSize: '12px', color: '#666', marginLeft: '6px' }}>
                      (optional for LinkedIn)
                    </span>
                  }
                </label>
                <div className="file-upload-area">
                  <input
                    type="file"
                    onChange={handleFileChange}
                    accept="image/*,video/*"
                    multiple={isMultiMediaPlatform()}
                  />
                  <Upload size={40} />
                  <div className="file-upload-text">
                    {formData.files.length > 0
                      ? `${formData.files.length} file(s) selected`
                      : (() => {
                          const isLinkedInOnly = formData.platforms.length === 1 && formData.platforms.includes('linkedin');
                          if (isLinkedInOnly) {
                            return 'Optional: Click to upload or drag and drop';
                          }
                          return isMultiMediaPlatform() 
                            ? 'Click to upload or drag and drop (up to 10 files)'
                            : 'Click to upload or drag and drop';
                        })()
                    }
                  </div>
                  {formData.files.length > 0 && (
                    <div className="file-list">
                      {formData.files.map((file, idx) => (
                        <div key={idx} className="file-item">
                          📎 {file.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {isUploading && (
                <div className="upload-progress">
                  <div className="progress-header">
                    <span>Uploading {formData.files.length} file(s)...</span>
                    <span className="progress-percent">{uploadProgress}%</span>
                  </div>
                  <div className="progress-bar">
                    <div 
                      className="progress-fill" 
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              )}

              <div className="modal-actions">
                <button 
                  className="btn-cancel" 
                  onClick={() => setShowModal(false)}
                  disabled={isUploading}
                >
                  Cancel
                </button>
                <button 
                  className="btn-submit" 
                  onClick={handleSubmit}
                  disabled={isUploading || !selectedCredential}
                >
                  {isUploading ? 'Scheduling...' : 'Schedule Post'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {openCreds && (
        <CredentialsModal onClose={() => setOpenCreds(false)} />
      )}
    </div>
  );
};

export default Dashboard;